import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { CaseRequirement, NoteResponse, UWBaseProps } from '@Underwriting/models';
import { UWNotesFeatureToggleDataService, UWRequirementsDataService, UWWorkbenchService } from '@Underwriting/services';
import { SubSink } from 'subsink';
import UWNotesAddForm, { UWNotesAddFormProps } from './components/uw-notes-add-form/uw-notes-add-form.component';
import UWNotesList from './components/uw-notes-list/uw-notes-list.component';
import { NgIf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ExcludeExcludedNotesFromMaster } from '@Underwriting/components/notes';
import { SharedModule } from '@Shared/shared.module';
import { CaseWorkbenchTaskNoteResponse, UWTaskNotesDataService } from '@Services';

@Component({
    selector: 'uw-notes',
    templateUrl: './uw-notes.component.html',
    styleUrls: ['./uw-notes.component.scss'],
    imports: [NgIf, MatProgressSpinner, ExcludeExcludedNotesFromMaster, SharedModule, UWNotesAddForm, UWNotesList]
})
export default class UWNotes implements OnInit {
    @Input() baseProps: UWBaseProps;
    @Output() onNoteAdded = new EventEmitter();

    emptyMessage = 'No Notes Added';
    noteRequiredMessage = 'Note is required';

    uwNotesAddForm: UWNotesAddFormProps = { dropShadow: true };
    notes: NoteResponse[] = [];
    notesLoaded = false;
    caseRequirements: CaseRequirement[] = [];
    caseRequirementsLoaded = false;
    caseTaskNotes: CaseWorkbenchTaskNoteResponse[] = [];
    caseTaskNotesLoaded = false;
    isEditingNote = false;
    updatedRequirementsTableDesign = false;

    subs = new SubSink();

    constructor(
        public _fb: UntypedFormBuilder,
        private uwWorkbenchService: UWWorkbenchService,
        private uwRequirementsDataService: UWRequirementsDataService,
        private uwNotesFeatureToggle: UWNotesFeatureToggleDataService,
        private uwTaskNotesDataService: UWTaskNotesDataService,
    ) { }

    private baseNotes: NoteResponse[] = [];

    //#region Helpers

    enrichNotesForCaseRequirements() {
        // Set the Case Requirement Name/Subtitle from the actual case requirement so it updates with SignalR updates
        this.notes.forEach(note => {
            const caseRequirement = this.caseRequirements.find(x => x.id === note.caseRequirementNote?.caseRequirementId);
            if (!caseRequirement) return;

            note.caseRequirementNote.caseRequirementName = caseRequirement.name;
            note.caseRequirementNote.caseRequirementSubtitle = caseRequirement.subtitle;
        });
    }

    private rebuildNotes() {
        this.notes = [...this.baseNotes, ...this.caseTaskNotes.map(n => this.mapTaskNoteToNoteResponse(n))];

        this.notes.sort((a, b) => {
            const aDate = a.audit?.createdOn || '';
            const bDate = b.audit?.createdOn || '';
            return bDate.localeCompare(aDate);
        });
    }

    private mapTaskNoteToNoteResponse(taskNote: CaseWorkbenchTaskNoteResponse): NoteResponse {
        return {
            id: taskNote.id,
            clientId: taskNote.clientId,
            caseId: taskNote.caseId,
            userDisplayName: taskNote.userDisplayName,
            userRole: taskNote.userRole,
            message: taskNote.message,
            isInternalNote: taskNote.isInternalNote,
            createdOnDisplay: taskNote.createdOnDisplay,
            updatedOnDisplay: taskNote.updatedOnDisplay,
            audit: taskNote.createdOn ? {
                createdBy: taskNote.createdBy,
                createdOn: taskNote.createdOn,
                updatedBy: taskNote.updatedBy,
                updatedOn: taskNote.updatedOn,
            } : undefined,
            caseTaskNote: { taskName: taskNote.taskName, taskId: taskNote.taskId, subtitle: taskNote.subtitle }
        };
    }

    //#endregion
    //#region Subscriptions

    subscribeToNotes() {
        this.subs.add(this.uwRequirementsDataService.loadNotes(this.baseProps.client.code, this.baseProps.case.id).subscribe(notes => {
            this.baseNotes = notes;
            this.notesLoaded = true;
            this.rebuildNotes();
            this.enrichNotesForCaseRequirements();
        }));
    }

    subscribeToCaseRequirements() {
        this.subs.add(this.uwRequirementsDataService.loadCaseRequirements(this.baseProps.client.code, this.baseProps.case.id).subscribe(caseRequirements => {
            this.caseRequirements = caseRequirements;
            this.caseRequirementsLoaded = true;
            this.enrichNotesForCaseRequirements();
        }));
    }

    subscribeToCaseTaskNotes() {
        this.subs.add(this.uwTaskNotesDataService.getCaseTaskNotesByCaseId(this.baseProps.case.id).subscribe(caseTaskNotes => {
            this.caseTaskNotes = caseTaskNotes;
            this.caseTaskNotesLoaded = true;
            this.rebuildNotes();
        }));
    }

    onEditingStateChanged(isEditing: boolean) {
        this.isEditingNote = isEditing;
    }

    //#endregion
    //#region Lifecycle

    ngOnInit() {
        this.uwWorkbenchService.setClientInfo(this.baseProps.client);
        this.subscribeToNotes();
        this.subscribeToCaseRequirements();

        this.subs.add(this.uwNotesFeatureToggle.loadHiddenNotesFeatureSetting(this.baseProps.account).subscribe((hiddenNoteFeatureSetting) => {
            this.updatedRequirementsTableDesign = hiddenNoteFeatureSetting.updatedRequirementsTableDesign;

            if (hiddenNoteFeatureSetting.duplicateTaskNotesInUWNotes) {                
                this.subscribeToCaseTaskNotes();
            }
        }));
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion
}
